'use client';

import { PDFEditorConnector } from '@ethsign/pdf-editor';
import { toast } from '@ethsign/ui';
import { ethSignAesDecrypt } from '@ethsign/utils-web';

import { useEffect } from 'react';

import { useParams } from 'react-router-dom';

import { DecryptContractModal, Loading, NoPermissionModal } from '@/components';
import { ShareContractModal } from '@/components/Modal/ShareContractModal';
import { GlobalConfig } from '@/constants';
import { useRouter, useSearchParams, useUserInfo } from '@/hooks';

import { globalVm } from '@/pages/GlobalPageCtrl';

import { ContractType } from '@/types';
import { decodeEmail, reportGAEvent, safeParseJSON } from '@/utils';

import { useViewModel } from '@/utils/bizify';

import { PageCtrl } from './PageCtrl';
import { Detail } from './components/Detail';
import { Header } from './components/Header';
import { Sign } from './components/Sign';

export default function Page(props: any) {
  const { isPreview } = props;

  const { id = '' } = useParams();
  const searchParams = useSearchParams();

  const email = decodeEmail(searchParams.email || '');
  const isView = searchParams.type === 'view';
  const { user } = useUserInfo();

  const vm = useViewModel(PageCtrl);
  const vmData = vm.$useSnapshot();

  const {
    fields,
    contract,
    permissionModalOpen,
    displayPassword,
    selectedField,
    file,
    xfdfStrings,
    subContractXfdfStrings,
    shareModalOpen
  } = vmData;

  const router = useRouter();
  const isNotSigned = contract?.clientStatus === 'wait_me';
  const isVisualContractView = isView && contract?.contractType === ContractType.visual_contract;
  const type = isNotSigned
    ? contract?.contractType === ContractType.link_sign_contract
      ? 'prepareAndSign'
      : 'sign'
    : 'preview';

  const isVoid = contract?.clientStatus === 'void';
  const isWaitMe = contract?.clientStatus === 'wait_me';
  const isShowAutoOpenPasswordManager =
    (contract?.contractType === ContractType.contract && !searchParams.email) || type === 'preview';
  const isShowDownload =
    !isVisualContractView && contract?.clientStatus === 'completed' && (contract?.recipientsTotal || 0) <= 100;

  useEffect(() => {
    vm.initInfo({ id, user, email, isView, isPreview, apiKey: searchParams.apiKey, password: searchParams.password });
  }, []);

  useEffect(() => {
    reportGAEvent('pc_contract_contractDetail', {
      address: user?.address || '',
      contractId: id,
      type
    });
  }, [type]);

  const onBackHome = () => {
    globalVm.setSavedParamsActive(true);
    const value = sessionStorage.getItem(GlobalConfig.SessionKeyOfDashboardSearch) || '';
    const folderId = safeParseJSON(value)?.folderId;
    const url = `/dashboard${folderId ? `?folderId=${folderId}` : ''}`;
    router.push(url);
  };

  const onSubmit = async (instance: PDFEditorConnector) => {
    const result = await vm.submit(instance!);
    if (result) {
      onBackHome();
    } else {
      toast({
        title: 'Error',
        description: 'Failed to sign! Please try again',
        variant: 'error'
      });
    }
  };

  return (
    <div className="w-full h-full ">
      {!isPreview && (
        <Header
          isShowDownload={isShowDownload}
          onDownload={() => {
            vm.downloadFile(contract.id);
          }}
          user={user}
          onShare={vm.onShare}
          onBack={onBackHome}
        ></Header>
      )}
      {vmData.loading && <Loading />}
      {!isWaitMe ? (
        <Detail
          key="detail"
          isPreview={isPreview}
          isVoid={isVoid}
          onDownload={(id) => {
            vm.downloadFile(id);
          }}
          subContractXfdfStrings={subContractXfdfStrings}
          isVisualContractView={isVisualContractView}
          onUserChange={(contractId: string) => {
            contractId && vm.getContract(contractId);
          }}
          onLoaded={() => {
            vm.setLoading(false);
          }}
          contract={contract}
          displayPassword={displayPassword}
          file={file}
          xfdfStrings={xfdfStrings}
          showPassword={vm.showPassword}
          viewMore={async () => {
            await vm.getRecipients(id);
          }}
          hasMore={vmData.recipientsHasMore}
        />
      ) : (
        <Sign
          key="sign"
          onSelectedFieldChange={vm.setSelectedField}
          onSubmit={onSubmit}
          onFieldsChange={vm.setFields}
          onLoaded={() => {
            vm.setLoading(false);
          }}
          fields={fields}
          contract={contract}
          onFieldClick={vm.onFieldClick}
          selectedField={selectedField}
          file={file}
          xfdfStrings={xfdfStrings}
          email={email}
        />
      )}
      <DecryptContractModal
        onClose={() => {
          vm.setModalOpen(false);
          !vmData.file && onBackHome();
        }}
        id={id}
        isShowAutoOpenPasswordManager={isShowAutoOpenPasswordManager}
        encryptedContractPassword={contract?.encryptedContractPassword}
        user={user}
        hidePasswordInput={!!vmData.file}
        open={vmData.modalOpen}
        onOpenChange={(open) => {
          vm.setModalOpen(open);
        }}
        onConfirm={async (value) => {
          try {
            if (vmData.file) {
              vm.setDisplayPassword(value);
            } else {
              const file = await ethSignAesDecrypt(vmData.encryptFile, value);
              vm.passwordConfirm(JSON.parse(file), value);
            }
            return true;
          } catch (error) {
            return false;
          }
        }}
      ></DecryptContractModal>
      <NoPermissionModal open={permissionModalOpen} onOpenChange={vm.setPermissionModalOpen}></NoPermissionModal>
      <ShareContractModal
        open={shareModalOpen}
        password={vmData.password}
        onOpenChange={vm.setShareModalOpen}
        contractId={contract?.id}
      />
    </div>
  );
}
